import { ScrollView, StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { StackNavigationProp } from '@react-navigation/stack';
import { StatusBar } from 'expo-status-bar';
import { SvgIconType } from '@thermenresorts/icons';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import { BathCardVariant } from 'src/domain/models/BathCardVariant';
import { useBathCardsStore } from 'src/domain/stores/useBathCardsStore';
import { useReserveStore } from 'src/domain/stores/useReserveStore';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import MenuRow from 'src/presentation/components/molecules/MenuRow';
import OptionRow from 'src/presentation/components/molecules/OptionRow';
import BathCardsSlider from 'src/presentation/components/organisms/BathCardsSlider';
import RemoveBathCardDialog from 'src/presentation/dialogs/RemoveBathCardDialog';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import useBathCardActions from 'src/presentation/hooks/useBathCardActions';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import type { BookingNavigation, BottomTabNavigation } from 'src/presentation/navigation/types/NavigationTypes';

export default function ReserveScreen() {
	const navigation = useNavigation<StackNavigationProp<BookingNavigation>>();
	const bottomTabNavigation = useNavigation<BottomTabNavigationProp<BottomTabNavigation>>();

	const { bathCards, cardsPage, hasError, getApiBathCards, openRemoveCardDialog, setCardsPage } = useBathCardsStore();
	const { openBathCardPurchase, openReservationWithBathCard } = useBathCardActions();
	const { openStandardReservation } = useReserveStore();
	const tabBarInset = useTabBarInset();

	const cards = bathCards ?? [];
	const hasSettledCards = bathCards !== null || hasError;

	useAppStateScreenListener({
		onFocussed: () => getApiBathCards(),
		onForegrounded: () => getApiBathCards(),
	});

	const openLoyaltyTab = () =>
		bottomTabNavigation.navigate(Constants.screenTabLoyalty, { screen: Constants.screenLoyalty });

	const openBathCardTab = () =>
		bottomTabNavigation.navigate(Constants.screenTabBathCard, { screen: Constants.screenBathCardsList });

	return (
		<View style={styles.container}>
			<StatusBar style="dark" />
			<BackgroundPattern />
			<RemoveBathCardDialog />

			<ScrollView contentContainerStyle={[styles.content, { paddingBottom: tabBarInset + Dimens.padding24 }]}>
				<BathCardsSlider
					cards={cards}
					page={cardsPage}
					setPage={setCardsPage}
					showHeader={cards.length > 1}
					variant={BathCardVariant.Summary}
					onPressBuy={() => openBathCardPurchase()}
					onPressRemove={(card) => openRemoveCardDialog(card)}
					onPressReserve={(card) => openReservationWithBathCard(card)}
					onPressView={() => openBathCardTab()}
				/>

				<View style={styles.options}>
					<OptionRow
						description={Translations.reserveWithLoyaltyDescription}
						icon={SvgIconType.starOutline}
						iconSize={Dimens.iconSize22}
						title={Translations.reserveWithLoyalty}
						onPress={() => openLoyaltyTab()}
					/>

					{hasSettledCards && cards.length === 0 ? (
						<OptionRow
							description={Translations.reserveWithBathCardDescription}
							icon={SvgIconType.cardAlt}
							iconSize={Dimens.iconSize18}
							title={Translations.reserveWithBathCard}
							onPress={() => openBathCardTab()}
						/>
					) : null}

					<OptionRow
						description={Translations.reserveStandardDescription}
						icon={SvgIconType.calendarRounded}
						iconSize={Dimens.iconSize18}
						title={Translations.reserveStandard}
						onPress={() => openStandardReservation()}
					/>
				</View>

				<View style={styles.reservations}>
					<MenuRow
						extraStyle={styles.reservationRow}
						icon={SvgIconType.calendarComplete}
						iconSize={Dimens.iconSize20}
						label={Translations.accountUpcomingReservations}
						last={true}
						onPress={() => navigation.navigate(Constants.screenUpcomingReservations)}
					/>
					<MenuRow
						extraStyle={styles.reservationRow}
						icon={SvgIconType.clockTimeBack}
						iconSize={Dimens.iconSize20}
						label={Translations.accountPastReservations}
						last={true}
						onPress={() => navigation.navigate(Constants.screenPastReservations)}
					/>
				</View>
			</ScrollView>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: Dimens.flex1,
	},
	content: {
		flexGrow: Dimens.flex1,
		gap: Dimens.gap16,
		paddingTop: Dimens.padding16,
	},
	options: {
		paddingHorizontal: Dimens.padding18,
		gap: Dimens.gap12,
	},
	reservations: {
		paddingHorizontal: Dimens.padding16,
	},
	reservationRow: {
		paddingVertical: Dimens.padding16,
	},
});
